import { useState, useEffect, useRef, useCallback } from "react";
import { Search, X } from "lucide-react";
import { Input } from "./ui/input";

/**
 * Reusable menu search bar. Filters DOM elements that have a
 * `data-menu-card-label` attribute by case-insensitive substring match
 * against the user's query. Cards whose label doesn't match are hidden
 * via inline style `display: none`. Clearing the query restores all.
 *
 * Usage:
 *   <MenuSearchBar containerSelector="[data-menu-grid]" placeholder="Search…" />
 *
 * And on each clickable card:
 *   <div data-menu-card-label="Group Logins">…</div>
 */
export default function MenuSearchBar({
  containerSelector,
  placeholder = "Search dashboard…",
  testId = "menu-search-input",
  className = "",
}) {
  const [query, setQuery] = useState("");
  const rafRef = useRef(null);

  const apply = useCallback((q) => {
    const root = containerSelector ? document.querySelector(containerSelector) : document;
    if (!root) return;
    const needle = q.trim().toLowerCase();
    // Cards are EITHER any element with data-menu-card-label OR any direct/grandchild
    // <button> or <a> inside the grid (covers all dashboards without annotating each).
    const explicit = root.querySelectorAll("[data-menu-card-label]");
    const generic = root.querySelectorAll(":scope > a, :scope > button, :scope > div > a, :scope > div > button");
    const candidates = new Set([...explicit, ...generic]);
    let visible = 0;
    candidates.forEach(el => {
      const label = (el.getAttribute("data-menu-card-label") || el.textContent || "").toLowerCase();
      const match = !needle || label.includes(needle);
      const target = el.closest("[data-sortable-card]") || el;
      target.style.display = match ? "" : "none";
      if (match) visible += 1;
    });
    // Empty state messaging
    const emptyEl = root.querySelector("[data-menu-empty]");
    if (emptyEl) emptyEl.style.display = (needle && visible === 0) ? "" : "none";
  }, [containerSelector]);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => apply(query));
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [query, apply]);

  // Clean up on unmount — make sure no card stays hidden after we leave
  useEffect(() => {
    return () => {
      const root = containerSelector ? document.querySelector(containerSelector) : document;
      if (!root) return;
      root.querySelectorAll("a, button, [data-menu-card-label], [data-sortable-card]").forEach(el => { el.style.display = ""; });
    };
  }, [containerSelector]);

  return (
    <div className={`relative ${className}`}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-9"
        data-testid={testId}
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Clear search"
          data-testid={`${testId}-clear`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
